// pages/Products.jsx
import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Package,
  TrendingUp,
  DollarSign,
  Archive,
} from "lucide-react";
import apiService from "../../services/api";
import CreateProductModal from "../../components/CreateProductModal";
import { useTheme } from "../../contexts/ThemeContext";

const Products = () => {
  const { isDarkMode } = useTheme();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    active: "all",
    productFamily: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchStats();
  }, [searchTerm, filters, pagination.page]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
      };

      if (filters.active !== "all") {
        params.active = filters.active === "active";
      }

      if (filters.productFamily) {
        params.productFamily = filters.productFamily;
      }

      const response = await apiService.getProducts(params);
      setProducts(response.data);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getProductStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleDelete = async (productId) => {
    if (
      !window.confirm(
        "Are you sure you want to archive this product? It will no longer appear in product lists.",
      )
    ) {
      return;
    }

    try {
      await apiService.deleteProduct(productId);
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error("Error deleting product:", error);
      alert("Failed to archive product");
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "—";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(value);
  };

  const getProductFamilies = () => {
    const families = new Set(
      products.map((p) => p.productFamily).filter(Boolean),
    );
    return Array.from(families);
  };

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-[#F4F5F7] text-gray-900"} p-6`}
    >
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1
              className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"} mb-2`}
            >
              Products
            </h1>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Manage your product catalog
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all hover:scale-105"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
            }}
          >
            <Plus className="w-5 h-5" />
            New Product
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-4`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Total Products
                  </p>
                  <p
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {stats.totalProducts}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </div>

            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-4`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Active Products
                  </p>
                  <p
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {stats.activeProducts}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </div>

            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-4`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Inactive Products
                  </p>
                  <p
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {stats.inactiveProducts}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
                  <Archive className="w-6 h-6 text-orange-500" />
                </div>
              </div>
            </div>

            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-4`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Product Families
                  </p>
                  <p
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {Object.keys(stats.productsByFamily || {}).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-500" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div
          className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-4`}
        >
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-500"} w-5 h-5`}
              />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500" : "bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-400"} border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>

            <select
              value={filters.active}
              onChange={(e) =>
                setFilters({ ...filters, active: e.target.value })
              }
              className={`px-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="all">All Products</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>

            <select
              value={filters.productFamily}
              onChange={(e) =>
                setFilters({ ...filters, productFamily: e.target.value })
              }
              className={`px-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="">All Families</option>
              {getProductFamilies().map((family) => (
                <option key={family} value={family}>
                  {family}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div
        className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg overflow-hidden`}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <Package
              className={`w-12 h-12 ${isDarkMode ? "text-gray-600" : "text-gray-400"} mx-auto mb-4`}
            />
            <p
              className={`text-lg mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              No products found
            </p>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              {searchTerm
                ? "Try adjusting your search"
                : "Get started by creating your first product"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead
                  className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-100 border-gray-200"} border-b`}
                >
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Product Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Family
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      List Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Cost Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}
                >
                  {products.map((product) => (
                    <tr
                      key={product.id}
                      className={`${isDarkMode ? "hover:bg-[#0C0E12]" : "hover:bg-gray-100"} transition-colors cursor-pointer`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div
                            className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {product.productName}
                          </div>
                          {product.productDescription && (
                            <div
                              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} truncate max-w-xs`}
                            >
                              {product.productDescription}
                            </div>
                          )}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        {product.productCode || "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.productFamily ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-500/10 text-blue-400">
                            {product.productFamily}
                          </span>
                        ) : (
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        {formatCurrency(product.listPrice)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        {formatCurrency(product.costPrice)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.active ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-500/10 text-green-400">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-500/10 text-gray-400">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {product.owner?.name?.charAt(0) || "?"}
                          </div>
                          <span
                            className={`ml-2 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {product.owner?.name || "Unassigned"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="relative inline-block">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(
                                activeDropdown === product.id
                                  ? null
                                  : product.id,
                              );
                            }}
                            className={`${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors p-1`}
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>

                          {activeDropdown === product.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setActiveDropdown(null)}
                              />
                              <div
                                className={`absolute right-0 mt-2 w-48 ${isDarkMode ? "bg-[#1a1d24] border-gray-700" : "bg-white border-gray-200"} border rounded-lg shadow-lg z-20`}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm ${isDarkMode ? "text-gray-300 hover:bg-[#0C0E12]" : "text-gray-700 hover:bg-gray-100"} flex items-center gap-2`}
                                >
                                  <Edit className="w-4 h-4" />
                                  Edit Product
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(product.id);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-[#0C0E12] flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Archive Product
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div
                className={`px-6 py-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex items-center justify-between`}
              >
                <div
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  of {pagination.total} products
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                    className={`px-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"} border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                    className={`px-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"} border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Product Modal */}
      {showCreateModal && (
        <CreateProductModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchProducts();
            fetchStats();
          }}
        />
      )}
    </div>
  );
};

export default Products;
